import { Spinner } from "components";
import { useLocation } from 'react-router-dom';
import { useState } from "react";
import { formatIndianNumber } from "utils";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import { FormField } from "pages/GenericOrder/ui-form/form-field";
import { Input } from "@/components/ui/input";
import { z } from "zod";
import { CurrencyInput } from "components/Input/Inputs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MultiSelect } from "components/MultiSelect/multi-select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ChargeRow } from "pages/GenericOrder/steps/calculation-panel";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useMutation, useQuery } from "react-query";
import { getProductList, refundForexOrder } from "../apis";
import { getCalculations } from "pages/GenericOrder/apis";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";

const formSchema = z.object({
  product: z.array(z.string()).min(1, 'At least one product must be selected'),
  reason: z.string().min(1, 'Reason is required'),
  quantity: z.number().min(1, 'Quantity must be at least 1'),
  transferMethod: z.string().min(1, 'Transfer method is required'),
  transactionId: z.string().min(1, 'Transaction ID is required'),
})

type FieldValues = z.infer<typeof formSchema>

const taxRates = {
  cgst: 0.09,
  sgst: 0.09,
}

const refundReasons = [
  { value: "Incomplete / incorrect documentation", label: "Incomplete / incorrect documentation" },
  { value: "Mismatch in traveler details", label: "Mismatch in traveler details" },
  { value: "Customer not willing to pay TCS", label: "Customer not willing to pay TCS" },
  { value: "Wrong product selected", label: "Wrong product selected" },
  {label:"Change of travel plan / trip cancelled", value: "Change of travel plan / trip cancelled" },
  {label:"Did not like the quality of currency notes", value: "Did not like the quality of currency notes" },
];

export default function ForexRefund() {
  const { id } = useParams()
  const location = useLocation();
  const cancelData = location.state as { cancelReason?: string; cancelAmount?: number };
  const form = useForm<FieldValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      product: [],
      reason: cancelData?.cancelReason || "", 
      quantity: cancelData?.cancelAmount || 0, 
      transferMethod: "source-ac",
      transactionId: "",
    },
  });
  const [productTotal, setProductTotal] = useState(0);
  const navigate = useNavigate();
  const [showAdditionalCharges, setShowAdditionalCharges] = useState(false);

  const { data: productList, isLoading: productLoading } = useQuery({
    queryKey: ['productList'],
    queryFn: () => getProductList(Number(id))
  })

  let { data: additionalCharges, isLoading: isLoadingAdditionalCharges } = useQuery({
    queryKey: ["additionalCharges"],
    queryFn: () =>
      getCalculations({ amount: productTotal, type: 'buy' }),
    enabled: form.watch('product')?.length > 0,
  })

  const myOptionsWithLabelandValue = productList?.product?.map(p => {
    return {
      label: p.product_detail?.name,
      value: p.id
    }
  })

  const { mutate, isLoading: loading } = useMutation({
    mutationFn: refundForexOrder,
    onSuccess: () => {
      toast.success('Refund request submitted successfully')
      setTimeout(() => navigate(`/forex/orderdetails/${id}`), 2000)
    },
    onError: () => {
      toast.error('Failed to submit refund request')
    }
  })

  const onSubmit = async (data: FieldValues) => {
    mutate({
      product_id: data.product,
      refund_reason: data.reason,
      refund_amount: data.quantity,
      txn_id: data.transactionId,
      order_id: Number(id),
      mode: data.transferMethod === 'Bank' ? 'offline' : 'online',
      refund_transfer: data.transferMethod,
    })
  }

  // const setTotalProductsValue = 

  return (
    <>
      {loading && <Spinner />}
      <div className="sm:p-1 flex font-inter items-start justify-between gap-5 overflow-hidden mx-auto w-full h-full">
        <section className="gap-5 bg-white-A700 w-full flex h-full flex-col p-3 rounded-lg">
          <div className="flex flex-col justify-between h-full gap-5 w-full sm:px-0 overflow-auto">
            <div className="flex relative flex-col gap-5 overflow-y-auto flex-grow">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 max-w-sm">
                  <div className=" space-y-3 px-2">
                   
                    <FormField
                      form={form}
                      name="product"
                      label="Select a product for refund"
                      render={({ field }) => (
                        <MultiSelect
                          options={myOptionsWithLabelandValue || []}
                          value={field.value as string[]}
                          onValueChange={value => {
                            field.onChange(value)
                            const totalProductValue = productList?.product?.reduce((acc, product) => {
                              if (value.includes(product.id.toString())) {
                                return acc + (product.inr_amount || 0);
                              }
                              return acc;
                            }, 0);
                            console.log(totalProductValue)
                            setProductTotal(totalProductValue || 0);
                            form.setValue('quantity', totalProductValue || 0)
                          }}
                          placeholder="Select a product for refund"
                          className="w-full h-full py-3.5"
                        />
                      )}
                    />
                    {/* Reason for refund */}
                    <FormField
                      form={form}
                      name="reason"
                      label="Enter reason for refund"
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger className="w-full p-3.5 h-full">
                            <SelectValue placeholder="Select a reason for refund" />
                          </SelectTrigger>
                          <SelectContent>
                            {refundReasons.map((reason) => (
                              <SelectItem key={reason.value} value={reason.value}>
                                {reason.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />

                    <FormField
                      form={form}
                      name='quantity'
                      label="Enter amount"
                      render={({ field }) => (
                        <CurrencyInput
                          field={{ ...field, onChange: e => field.onChange(parseInt(e.target.value, 10)) }}
                          currencyType={'INR'}
                        />
                      )}
                    />
                    <FormField
                      form={form}
                      name="transferMethod"
                      label="Refund transfer to"
                      render={({ field }) => (
                        <RadioGroup
                          className="flex w-full"
                          value={String(field.value || "")}
                          onValueChange={field.onChange}
                        >
                          <label
                            htmlFor="Source"
                            className={`${field.value === "Source A/C" ? " focus-within:border-blue-700 border-black-label" : ""} flex w-full items-center rounded-lg border p-3 space-x-2`}
                          >
                            <RadioGroupItem
                              className="w-4 h-4 text-blue-600 checked:ring-4 checked:ring-offset-4 checked:ring-offset-blue-700 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2"
                              value="Source A/C"
                              id="Source"
                            />
                            <p>Source A/C</p>
                          </label>
                          <label
                            htmlFor="Other"
                            className={`${(field.value === "Other" || ['UPI', 'Bank A/C'].includes(form.watch("transferMethod"))) ? " focus-within:border-blue-700 border-blue-700" : ""} flex w-full items-center rounded-lg border p-3 space-x-2`}
                          >
                            <RadioGroupItem
                              className="w-4 h-4 text-blue-600 checked:ring-4 checked:ring-offset-4 checked:ring-offset-blue-700 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2"
                              value="Other"
                              id="Other"
                            />
                            <p>Other</p>
                          </label>
                        </RadioGroup>
                      )}
                    />
                    {
                      ["Other", 'UPI', 'Bank A/C'].includes(form.watch("transferMethod")) &&
                      <FormField
                        form={form}
                        name="transferMethod"
                        label="Refund transfer to"
                        render={({ field }) => (
                          <RadioGroup
                            className="flex w-full"
                            value={String(field.value || "")}
                            onValueChange={field.onChange}
                          >
                            <label
                              htmlFor="UPI"
                              className={`${field.value === "UPI" ? " focus-within:border-blue-700 border-black-label" : ""} flex w-full items-center rounded-lg border p-3 space-x-2`}
                            >
                              <RadioGroupItem
                                className="w-4 h-4 text-blue-600 checked:ring-4 checked:ring-offset-4 checked:ring-offset-blue-700 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2"
                                value="UPI"
                                id="UPI"
                              />
                              <p>UPI</p>
                            </label>
                            <label
                              htmlFor="Bank"
                              className={`${field.value === "Bank A/C" ? " focus-within:border-blue-700 border-black-label" : ""} flex w-full items-center rounded-lg border p-3 space-x-2`}
                            >
                              <RadioGroupItem
                                className="w-4 h-4 text-blue-600 checked:ring-4 checked:ring-offset-4 checked:ring-offset-blue-700 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2"
                                value="Bank A/C"
                                id="Bank"
                              />
                              <p>Bank A/C</p>
                            </label>
                          </RadioGroup>
                        )}
                      />
                    }

                    <FormField
                      form={form}
                      name="transactionId"
                      label="Transaction ID"
                      render={({ field }) => (
                        <Input
                          {...field}
                          placeholder="Transaction ID"
                          className="w-full h-full p-3.5"
                        />
                      )}
                    />

                    <div className="flex gap-3 sm:flex-col sm:items-start">
                      {
                        <button
                          type="submit"
                          className="px-4 py-[11px] w-fit disabled:opacity-40 text-sm font-semibold rounded-lg text-white-A700 border border-blue-700 bg-blue-700 "
                        >
                          Submit
                        </button>
                      }
                    </div>
                  </div>
                </form>
              </Form>
            </div>
          </div>
        </section>
        <section className=" bg-white-A700 h-full rounded-lg w-1/3 p-3">
          <div className="w-full bg-white rounded-xl p-6 shadow-[0px_4px_4px_2px_rgba(239,241,251,1)]">
            <h2 className="text-2xl font-bold mb-6">Calculation</h2>
            <div className="flex flex-col gap-8">
              {/* Product breakdown */}
              <div className="flex flex-col gap-3">
                {productList?.product?.filter(p => form.watch('product').includes(p.id.toString()))?.map((item, index) => (
                  <div key={index}>
                    <div className="flex justify-between items-center">
                      <span className="font-inter font-medium text-sm leading-none tracking-[-0.04em] text-gray-800">
                        <span className="text-[#8D8D8D] text-sm font-normal leading-[112%]">{item?.country_currency_want}</span> {"  "}{item?.quantity} <span className="text-[#5777B2]">@</span> <span className="text-[#8D8D8D] text-sm font-normal leading-[112%]">INR</span> {item?.rate_and_margins?.[0]?.ibr_rate}
                      </span>
                      <div className="flex items-center">
                        <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2 py-0.5 rounded-md mr-2">
                          {item?.country_currency_want}
                        </span>
                        <span className="font-inter font-medium text-base leading-[16px] tracking-[-0.01em]">
                          {item?.inr_amount.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                    </div>
                    {index !== productList?.product.length - 1 && (
                      <div className="w-full h-px bg-gray-100" />
                    )}
                  </div>
                ))}
              </div>
              {/* Additional charges section */}
              <div className="flex flex-col gap-5">
                <div
                  className="flex items-center justify-between h-[23px] rounded-[12px] pt-1 pr-3 pb-1 pl-3 bg-gray-50 font-medium cursor-pointer select-none"
                  onClick={() => setShowAdditionalCharges(!showAdditionalCharges)}
                >
                  <span className="font-medium">Additional charges</span>
                  {showAdditionalCharges ? (
                    <ChevronUp size={20} />
                  ) : (
                    <ChevronDown size={20} />
                  )}
                </div>

                {showAdditionalCharges && (
                  <div className="space-y-0 mt-2">
                    {isLoadingAdditionalCharges ? (
                      <div className="text-center text-gray-400 py-4">
                        Loading charges...
                      </div>
                    ) : !additionalCharges ? (
                      <div className="text-center text-gray-400 py-4">
                        No additional charges available
                      </div>
                    ) : (
                      <>
                        <ChargeRow
                          label={`CGST @ ${(taxRates?.cgst || 0) * 100}%`}
                          amount={additionalCharges?.CGST}
                        />
                        <ChargeRow
                          label={`SGST @ ${(taxRates?.sgst || 0) * 100}%`}
                          amount={additionalCharges?.SGST}
                        />
                        <ChargeRow
                          label="Round off"
                          amount={additionalCharges?.roundOff}
                        />
                      </>
                    )}
                  </div>
                )}

                {/* Grand Total */}
                <div className="flex justify-between items-center px-[15px] py-[17px] bg-[#f2f6ff] border-t-2 border-[#176bef] rounded-b-xl">
                  <span className="font-inter font-semibold text-base leading-none text-[#176bef]">
                    Grand Total
                  </span>
                  <div className="flex items-center">
                    <span className="bg-[#176bef] text-white-A700 w-[29px] h-[19px] rounded-[8px] px-[6px] flex items-center justify-center">
                      <span className="font-inter font-normal text-[10px] leading-[1.12] tracking-[-0.01em]">
                        INR
                      </span>
                    </span>
                    <span className="font-inter font-medium text-base leading-[1.5] text-[#176bef] ml-3">
                      {(additionalCharges?.total_payable?.grand_total || '0.00')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className=" flex-1 h-full flex justify-end items-end ">
            <div className="flex flex-col gap-3 p-2">
              <p className="font-normal leading-[19.2px] text-xs text-[#666666]">
                The above amount is an estimate and may change based on
                real time fx rates and remitter's current FY transactions.
              </p>
              <h4 className="font-medium leading-[19.2px] text-xs text-[##232323]">
                Decimal are rounded off.
              </h4>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
